export default function MaterialsStructure() {
  const materials = [
    {
      icon: 'ri-building-2-line',
      title: 'Main Frame Structure',
      specs: 'Reinforced Metal Framework',
      description: 'High-strength zinc steel frame with anti-corrosion treatment, engineered for long-term durability and structural integrity',
      features: ['Seismic resistance', 'Wind load rated', 'Corrosion protected', '15+ year lifespan']
    },
    {
      icon: 'ri-shield-line',
      title: 'Exterior Wall System',
      specs: 'Premium Aluminium Panels',
      description: 'High-quality aluminium composite panels with protective coating, weather-resistant and low-maintenance finish',
      features: ['UV resistant coating', 'Scratch resistant', 'Color retention', 'Easy maintenance']
    },
    {
      icon: 'ri-window-line',
      title: 'Glass System',
      specs: 'Multi-Layer Insulated Glass',
      description: 'Tempered safety glass with multi-layer insulation for superior sound reduction and thermal performance',
      features: ['Sound insulation 35dB+', 'Thermal efficiency', 'UV protection', 'Safety certified']
    },
    {
      icon: 'ri-temp-hot-line',
      title: 'Thermal Insulation',
      specs: 'Advanced Insulation System',
      description: 'High-performance insulation materials ensuring year-round comfort and energy efficiency in all climates',
      features: ['R-value optimised', 'Moisture barrier', 'Fire resistant', 'Energy efficient']
    }
  ];

  const specifications = [
    { label: 'Frame Material', value: 'Galvanized steel frame structure 5.0' },
    { label: 'Exterior Shell', value: 'Aviation Aluminium Plates' },
    { label: 'Glass Type', value: 'Low-E Double Glazed' },
    { label: 'Waterproofing', value: 'Yes' },
    { label: 'Heat Preservation', value: 'Polyurethane insulation layer' },
    { label: 'Heat Insulation', value: 'Yes' },
    { label: 'Wind and Earthquake Resistance', value: 'Yes' },
    { label: 'Service Life', value: '15+ Years' }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 w-full">
      <div className="max-w-7xl mx-auto w-full">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-thin mb-4 sm:mb-6 px-4">
            Premium <span className="text-gray-300">Materials</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto px-4">
            Built to last with industry-leading materials and construction
          </p>
        </div>

        {/* Materials Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16 lg:mb-20 w-full">
          {materials.map((material, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-gray-500/20 rounded-2xl p-8 hover:border-gray-400/50 hover:shadow-[0_0_30px_rgba(148,163,184,0.15)] transition-all duration-500 w-full"
            >
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gray-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className={`${material.icon} text-3xl text-gray-300`}></i>
                </div>
                
                <div className="flex-1">
                  <h3 className="text-2xl font-light text-white mb-2">{material.title}</h3>
                  <div className="inline-block px-3 py-1 bg-gray-500/20 rounded-full text-xs text-gray-400 mb-4">
                    {material.specs}
                  </div>
                  <p className="text-gray-400 mb-6 leading-relaxed">{material.description}</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {material.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-5 h-5 bg-gray-500/20 rounded-full flex items-center justify-center">
                          <i className="ri-check-line text-gray-300 text-xs"></i>
                        </div>
                        <span className="text-sm text-gray-400">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Technical Specifications */}
        <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-gray-500/20 rounded-3xl p-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
            <div>
              <h3 className="text-3xl font-light text-white mb-6">
                Technical <span className="text-gray-300">Specifications</span>
              </h3>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Every component is selected for maximum performance, safety, and longevity. Our materials meet or exceed international building standards for commercial structures.
              </p>
              
              <div className="space-y-4">
                {specifications.map((spec, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-3 border-b border-gray-500/20"
                  >
                    <span className="text-gray-400">{spec.label}</span>
                    <span className="text-white font-medium">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative w-full h-[500px]">
              <img 
                src="/images/ai-technical-architectural-diagram-of-modul-19f027.webp"
                alt="Structure Details"
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>

        {/* Bottom Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-gray-500/20 rounded-2xl p-6 text-center">
            <div className="w-14 h-14 bg-gray-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-check-line text-2xl text-gray-300"></i>
            </div>
            <h4 className="text-lg font-medium text-white mb-2">Safety Certified</h4>
            <p className="text-sm text-gray-400">Meets international building and safety standards</p>
          </div>
          
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-gray-500/20 rounded-2xl p-6 text-center">
            <div className="w-14 h-14 bg-gray-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-time-line text-2xl text-gray-300"></i>
            </div>
            <h4 className="text-lg font-medium text-white mb-2">Long Service Life</h4>
            <p className="text-sm text-gray-400">15+ years durability with minimal maintenance</p>
          </div>
          
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-gray-500/20 rounded-2xl p-6 text-center">
            <div className="w-14 h-14 bg-gray-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-leaf-line text-2xl text-gray-300"></i>
            </div>
            <h4 className="text-lg font-medium text-white mb-2">Eco-Friendly</h4>
            <p className="text-sm text-gray-400">Sustainable materials and energy-efficient design</p>
          </div>
        </div>
      </div>
    </section>
  );
}